import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BaseLayout from "@/v2/components/BaseLayout";
import ArticleCard from "@/v2/components/ArticleCard";
import { fetchCategoryArticles } from "@/v2/queries";
import { isV2Category, V2_CATEGORY_LABELS } from "@/v2/categories";
import NotFound from "@/pages/NotFound";

const SITE = "https://korthet.se";

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();

  if (!isV2Category(category)) {
    return <NotFound />;
  }

  const label = V2_CATEGORY_LABELS[category];
  const { data, isLoading, error } = useQuery({
    queryKey: ["v2", "category", category],
    queryFn: () => fetchCategoryArticles(category),
  });

  const canonicalUrl = `${SITE}/${category}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Korthet",
      url: `${SITE}/`,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE}/sok?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Korthet",
      url: `${SITE}/`,
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${label} – Korthet`,
      url: canonicalUrl,
      inLanguage: "sv-SE",
      isPartOf: { "@type": "WebSite", name: "Korthet", url: `${SITE}/` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: label, item: canonicalUrl },
      ],
    },
  ];

  return (
    <BaseLayout
      title={`${label} – Korthet`}
      description={`Senaste nyheterna inom ${label.toLowerCase()}, sammanfattade av AI.`}
      canonicalPath={`/${category}`}
      jsonLd={jsonLd}
    >
      <h1 className="k2-h1">{label}</h1>
      {isLoading && <p className="k2-loading">Laddar…</p>}
      {error && <p className="k2-error">Kunde inte hämta artiklar.</p>}
      {data && data.length === 0 && (
        <p className="k2-empty">Inga artiklar i {label.toLowerCase()} ännu.</p>
      )}
      {data && data.length > 0 && (
        <ul className="k2-feed">
          {data.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </ul>
      )}
    </BaseLayout>
  );
}